<template>
  <TransitionRoot as="template" :show="open">
    <Dialog as="div" class="fixed z-10 inset-0 overflow-y-auto" @close="closeModal">
      <div
        class="flex items-end justify-center min-h-screen px-4 pb-20 text-center sm:block sm:p-0"
      >
        <TransitionChild
          as="template"
          enter="ease-out duration-300"
          enter-from="opacity-0"
          enter-to="opacity-100"
          leave="ease-in duration-200"
          leave-from="opacity-100"
          leave-to="opacity-0"
        >
          <DialogOverlay class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </TransitionChild>

        <!-- This element is to trick the browser into centering the modal contents. -->
        <span class="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">
          &#8203;
        </span>
        <TransitionChild
          as="template"
          enter="ease-out duration-300"
          enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          enter-to="opacity-100 translate-y-0 sm:scale-100"
          leave="ease-in duration-200"
          leave-from="opacity-100 translate-y-0 sm:scale-100"
          leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
        >
          <div
            class="
              inline-block
              align-bottom
              bg-white
              rounded-lg
              px-4
              pb-4
              text-left
              overflow-hidden
              shadow-xl
              transform
              transition-all
              sm:my-8
              sm:align-middle
              sm:max-w-5xl
              sm:w-full
              sm:p-12
            "
          >
            <div>
              <div class="mt-3 sm:mt-12">
                <h2 class="mb-4 text-xl leading-7 font-bold text-vice-teal-900">
                  Before we start...
                </h2>
                <p class="text-md font-medium text-vice-dark-teal mt-1">
                  The remote consultation you are about to join is a live consultation with a real
                  patient. It requires you to meet the
                  <strong class="font-bold">professional standards</strong>
                  expected in all clinician encounters.
                </p>
              </div>
              <div class="flex flex-row gap-1 w-full">
                <ul
                  role="list"
                  class="
                    divide-y divide-gray-300
                    bg-vice-light-grey
                    py-7
                    px-6
                    rounded-l-lg
                    my-7
                    w-1/2
                  "
                >
                  <li class="pb-5 text-md font-bold text-vice-dark-teal">
                    These responsibilities include:
                  </li>
                  <li class="py-4 flex items-center">
                    <img class="h-12 w-16" src="/image/icons/responsibilities-icon.svg" />
                    <p class="ml-10 text-md font-medium text-vice-dark-grey">
                      Professional and ethical responsibilities
                    </p>
                  </li>
                  <li class="py-4 flex items-center">
                    <img class="h-12 w-16" src="/image/icons/scale-of-justice-icon.svg" />
                    <p class="ml-10 text-md font-medium text-vice-dark-grey">
                      Legal responsibilities
                    </p>
                  </li>
                  <li class="py-4 flex items-center">
                    <img class="h-12 w-16" src="/image/icons/quality-assurance-icon.svg" />
                    <p class="ml-10 text-md font-medium text-vice-dark-grey">
                      Patient safety and quality improvement
                    </p>
                  </li>
                </ul>

                <ul
                  role="list"
                  class="
                    divide-y divide-gray-300
                    bg-vice-light-grey
                    py-7
                    px-6
                    w-1/2
                    rounded-r-lg
                    my-7
                  "
                >
                  <li class="pb-5 text-md font-bold text-vice-dark-teal">You will be asked to:</li>
                  <li class="py-4 flex items-center">
                    <img class="h-12 w-16" src="/image/icons/uncertainty-icon.svg" />
                    <p class="ml-10 text-md font-medium text-vice-dark-grey">
                      Deal with complexity and uncertainty
                    </p>
                  </li>
                  <li class="py-4 flex items-center">
                    <img class="h-12 w-16" src="/image/icons/safeguard-icon.svg" />
                    <p class="ml-10 text-md font-medium text-vice-dark-grey">
                      Safeguard vulnerable patients
                    </p>
                  </li>
                  <li class="py-4 flex items-center">
                    <img class="h-12 w-16" src="/image/icons/team-icon.svg" />
                    <p class="ml-10 text-md font-medium text-vice-dark-grey">
                      Show leadership and team working
                    </p>
                  </li>
                </ul>
              </div>
            </div>
            <div class="flex justify-center gap-2 items-center">
              <input
                data-cy="agree-terms-entry-modal"
                id="agree-checkbox"
                type="checkbox"
                @click="checkbox = !checkbox"
                class="
                  focus:ring-vice-teal-500
                  cursor-pointer
                  h-4
                  w-4
                  text-vice-teal-600
                  border-gray-300
                  rounded
                "
              />
              <label for="agree-checkbox" class="text-md text-vice-dark-grey font-bold">
                I have read the
                <a :href="route('dashboard', { tab: 'terms-of-use' })" target="_blank">
                  <span class="text-vice-teal-400 underline">Terms of Use</span>
                </a>
                and am aware of my responsibilities and will act upon them.
              </label>
            </div>
            <div class="mt-5 sm:mt-6 flex justify-around">
              <inertia-link
                :href="checkbox ? route('session.show', session.unique_id) : 'javascript:void(0)'"
                :class="
                  checkbox
                    ? 'bg-vice-rose-400 hover:bg-vice-rose-500 text-white'
                    : 'pointer-events-none text-gray-700'
                "
                type="button"
                @click="closeModal"
                class="
                  inline-flex
                  items-center
                  px-14
                  py-3
                  bg-gray-150
                  shadow-sm
                  text-base
                  font-medium
                  rounded-md
                  focus:outline-none
                  focus:ring-2 focus:ring-offset-2 focus:ring-vice-dark-grey
                "
              >
                <LogoutIcon class="w-6 h-5"></LogoutIcon>
                Enter session
              </inertia-link>
            </div>
          </div>
        </TransitionChild>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<script>
import { ref } from 'vue';
import { usePage } from '@inertiajs/inertia-vue3';
import { Dialog, DialogOverlay, TransitionChild, TransitionRoot } from '@headlessui/vue';
import { LogoutIcon } from '@heroicons/vue/solid';
// import useTourManager from '@/Composables/useTourManager';
// import { userEntryModalTourSteps } from '@/Data/tourSteps';

// const userEntryModalTour = useTourManager('userEntryModalTour', userEntryModalTourSteps);

export default {
  name: 'ParticipantChecklist',
  props: {
    open: {
      type: Boolean,
      required: true,
    },
  },
  components: {
    Dialog,
    DialogOverlay,
    TransitionChild,
    TransitionRoot,
    LogoutIcon,
  },
  // watch: {
  //   open: {
  //     handler() {
  //       if (this.open) {
  //         setTimeout(() => {
  //           userEntryModalTour.startTour();
  //         }, 500);
  //       }
  //     },
  //   },
  // },
  emits: ['closeEntryModal'],
  setup(props, { emit }) {
    const pageProps = usePage().props.value;
    const { session } = pageProps;
    const checkbox = ref(false);

    const closeModal = () => {
      checkbox.value = false;
      emit('closeEntryModal');
    };

    return { checkbox, session, closeModal };
  },
};
</script>

<style scoped></style>
